import { collection, query, getDocs, doc, getDoc, where, or, deleteDoc } from 'firebase/firestore';
import { db } from '../config';
import { handleFirebaseError } from '../error-handling';
import type { User } from '@/types';

export async function getUserById(userId: string): Promise<User | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return null;
    }

    const data = userDoc.data();
    return {
      id: userDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      roles: data.roles?.map((role: any) => ({
        ...role,
        assignedAt: role.assignedAt?.toDate() || new Date()
      }))
    } as User;
  } catch (error) {
    handleFirebaseError(error, 'Error fetching user');
    return null;
  }
}

export async function getAllUsers(): Promise<User[]> {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        roles: Array.isArray(data.roles) ? data.roles.map((role: any) => ({
          ...role,
          assignedAt: role.assignedAt?.toDate() || new Date()
        })) : []
      } as User;
    });
  } catch (error) {
    handleFirebaseError(error, 'Error fetching users');
    return [];
  }
}

export async function getUsersByRole(role: string): Promise<User[]> {
  try {
    // Use a more efficient query with proper indexing
    const usersRef = collection(db, 'users');
    const usersQuery = query(usersRef);
    const snapshot = await getDocs(usersQuery);
    
    // Filter by role in memory for now (until we can use array-contains-any)
    return snapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          roles: Array.isArray(data.roles) ? data.roles.map((role: any) => ({
            ...role,
            assignedAt: role.assignedAt?.toDate() || new Date()
          })) : []
        } as User;
      })
      .filter(user => 
        user.roles?.some(r => r.role === role && r.isActive)
      );
  } catch (error) {
    handleFirebaseError(error, 'Error fetching users by role');
    return [];
  }
}

export interface Instructor {
  id: string;
  name: string;
  nameEn: string;
  nameAr: string;
  email: string;
}

export async function getInstructors(): Promise<Instructor[]> {
  try {
    // First get all users
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    // Filter users who have the instructor role
    const instructors = snapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      })
      .filter(user => 
        Array.isArray(user.roles) && 
        user.roles.some((role: any) => role.role === 'instructor' && role.isActive)
      )
      .map(user => {
        const nameEn = `${user.firstNameEn || ''} ${user.lastNameEn || ''}`.trim();
        const nameAr = `${user.firstNameAr || ''} ${user.lastNameAr || ''}`.trim();
        const fallbackName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        
        return {
          id: user.id,
          name: nameEn || fallbackName || user.email, // Default to English name or fallback
          nameEn: nameEn || fallbackName || user.email,
          nameAr: nameAr || fallbackName || user.email,
          email: user.email
        };
      });

    return instructors;
  } catch (error) {
    handleFirebaseError(error, 'Error fetching instructors');
    throw error;
  }
}

export async function deleteUser(userId: string): Promise<void> {
  try {
    console.log(`🗑️ Starting comprehensive deletion for user: ${userId}`);
    
    // Get user data before deletion to retrieve phone number
    const user = await getUserById(userId);
    
    // Get all related data that needs to be deleted
    const deletionPromises = [];
    
    // 1. Delete organization memberships
    const membershipsQuery = query(collection(db, 'organizationMembers'), where('userId', '==', userId));
    const membershipsSnapshot = await getDocs(membershipsQuery);
    membershipsSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${membershipsSnapshot.docs.length} organization memberships to delete`);
    
    // 2. Delete user's habits
    const habitsQuery = query(collection(db, 'habits'), where('userId', '==', userId));
    const habitsSnapshot = await getDocs(habitsQuery);
    habitsSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${habitsSnapshot.docs.length} habits to delete`);
    
    // 3. Delete user's exams
    const examsQuery = query(collection(db, 'exams'), where('customerId', '==', userId));
    const examsSnapshot = await getDocs(examsQuery);
    examsSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${examsSnapshot.docs.length} exams to delete`);
    
    // 4. Delete user's group memberships
    const groupMembersQuery = query(collection(db, 'groupMembers'), where('userId', '==', userId));
    const groupMembersSnapshot = await getDocs(groupMembersQuery);
    groupMembersSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${groupMembersSnapshot.docs.length} group memberships to delete`);
    
    // 5. Delete user's appointments
    const appointmentsQuery = query(collection(db, 'appointments'), where('customerId', '==', userId));
    const appointmentsSnapshot = await getDocs(appointmentsQuery);
    appointmentsSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${appointmentsSnapshot.docs.length} appointments to delete`);
    
    // 6. Delete user's treatment support requests
    const supportRequestsQuery = query(collection(db, 'treatmentSupportRequests'), where('userId', '==', userId));
    const supportRequestsSnapshot = await getDocs(supportRequestsQuery);
    supportRequestsSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${supportRequestsSnapshot.docs.length} treatment support requests to delete`);
    
    // 7. Delete user's posts (if any)
    const postsQuery = query(collection(db, 'posts'), where('userId', '==', userId));
    const postsSnapshot = await getDocs(postsQuery);
    postsSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${postsSnapshot.docs.length} posts to delete`);
    
    // 8. Delete user's rewards/points (if any)
    const pointsQuery = query(collection(db, 'points'), where('userId', '==', userId));
    const pointsSnapshot = await getDocs(pointsQuery);
    pointsSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${pointsSnapshot.docs.length} points records to delete`);
    
    // 9. Delete user's hierarchical memberships (if any)
    const hierarchicalMembersQuery = query(collection(db, 'hierarchicalMembers'), where('userId', '==', userId));
    const hierarchicalMembersSnapshot = await getDocs(hierarchicalMembersQuery);
    hierarchicalMembersSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${hierarchicalMembersSnapshot.docs.length} hierarchical memberships to delete`);
    
    // 10. Delete user's virtual memberships (if any)
    const virtualMembersQuery = query(collection(db, 'virtualMembers'), where('userId', '==', userId));
    const virtualMembersSnapshot = await getDocs(virtualMembersQuery);
    virtualMembersSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${virtualMembersSnapshot.docs.length} virtual memberships to delete`);
    
    // 11. Delete user's score sync records (if any)
    const scoreSyncQuery = query(collection(db, 'scoreSync'), where('userId', '==', userId));
    const scoreSyncSnapshot = await getDocs(scoreSyncQuery);
    scoreSyncSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${scoreSyncSnapshot.docs.length} score sync records to delete`);
    
    // 12. Delete user's manual sync triggers (if any)
    const manualSyncQuery = query(collection(db, 'manualSyncTriggers'), where('userId', '==', userId));
    const manualSyncSnapshot = await getDocs(manualSyncQuery);
    manualSyncSnapshot.docs.forEach(doc => {
      deletionPromises.push(deleteDoc(doc.ref));
    });
    console.log(`📋 Found ${manualSyncSnapshot.docs.length} manual sync triggers to delete`);
    
    // 13. Delete user's phone reservation (if any)
    if (user?.phone) {
      const phoneRef = doc(db, 'phones', user.phone);
      const phoneDoc = await getDoc(phoneRef);
      
      if (phoneDoc.exists()) {
        const phoneData = phoneDoc.data();
        // Only delete if this phone belongs to the user being deleted
        if (phoneData?.userId === userId) {
          deletionPromises.push(deleteDoc(phoneRef));
          console.log(`📋 Found phone reservation to delete: ${user.phone}`);
        }
      }
    }
    
    // 14. Finally, delete the user document itself
    const userRef = doc(db, 'users', userId);
    deletionPromises.push(deleteDoc(userRef));
    
    // Execute all deletions in parallel
    await Promise.all(deletionPromises);
    
    console.log(`✅ Successfully deleted user ${userId} and all related data (${deletionPromises.length} total deletions)`);
    
    // Note: Firebase Auth user deletion should be handled separately
    // This function only deletes the Firestore documents
    // To delete the Firebase Auth user, you would need to use the Firebase Admin SDK
    // or call the Firebase Auth REST API with appropriate permissions
  } catch (error) {
    console.error(`❌ Error deleting user ${userId}:`, error);
    handleFirebaseError(error, 'Error deleting user and related data');
    throw error;
  }
}

/**
 * Comprehensive user deletion that includes both Firestore data and Firebase Auth user
 * Note: This requires Firebase Admin SDK and should be used server-side only
 */
export async function deleteUserCompletely(userId: string): Promise<void> {
  try {
    console.log(`🗑️ Starting complete user deletion (Firestore + Auth) for user: ${userId}`);
    
    // First delete all Firestore data
    await deleteUser(userId);
    
    // Then delete the Firebase Auth user (requires Admin SDK)
    // This would typically be done in a Cloud Function or server-side
    console.log(`⚠️ Firebase Auth user deletion requires Admin SDK - implement server-side`);
    
    console.log(`✅ Complete user deletion process initiated for ${userId}`);
  } catch (error) {
    console.error(`❌ Error in complete user deletion for ${userId}:`, error);
    throw error;
  }
}